import React, { useState, useEffect } from 'react';
import { RefreshCw, ClipboardList, MessageCircle, Search, ChevronDown, ChevronLeft, ChevronsLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { fetchWithAuth, API_BASE_URL } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSocket } from '../../hooks/useSocket';
import { useNavigate } from 'react-router-dom';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, DatePicker } from '../ui';

export default function GreetingsLogView({ currentUser }) {
  const { t } = useLanguage();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [greetingsLog, setGreetingsLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [grandTotal, setGrandTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, success: 0, failed: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchGreetingsLog = async () => {
    setLoadingLog(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        search: searchTerm,
        status: filterStatus,
        fromDate: fromDate,
        toDate: toDate
      });
      const res = await fetchWithAuth(`${API_BASE_URL}/whatsapp/greetings-log?${queryParams.toString()}`);
      const json = await res.json();
      if (json.status) {
        setGreetingsLog(json.data || []);
        setTotalCount(json.total || 0);
        if (json.grandTotal !== undefined) {
          setGrandTotal(json.grandTotal);
        }
        if (json.pagination) {
          setTotalPages(json.pagination.totalPages || 1);
        } else {
          setTotalPages(Math.max(1, Math.ceil((json.total || 0) / pageSize)));
        }
        if (json.counts) {
          setCounts(json.counts);
        } else {
          setCounts({ all: json.total || 0, success: 0, failed: 0 });
        }
      }
    } catch (err) {
      console.error('Failed to load Greetings Log:', err);
    } finally {
      setLoadingLog(false);
    }
  };

  useEffect(() => {
    fetchGreetingsLog();
  }, [currentPage, pageSize, filterStatus, fromDate, toDate]);

  useEffect(() => {
    if (!socket) return;
    const handleRealtimeUpdate = () => {
      fetchGreetingsLog();
    };
    socket.on('greeting_log_updated', handleRealtimeUpdate);
    socket.on('activity_added', handleRealtimeUpdate);
    socket.on('lead_created', handleRealtimeUpdate);
    return () => {
      socket.off('greeting_log_updated', handleRealtimeUpdate);
      socket.off('activity_added', handleRealtimeUpdate);
      socket.off('lead_created', handleRealtimeUpdate);
    };
  }, [socket, currentPage, pageSize, filterStatus, fromDate, toDate, searchTerm]);

  // Debounced Search trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchGreetingsLog();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header — matching ContactsView & LeadsView exactly */}
      <div className="mb-4">
        <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
          {t('whatsappAutoGreetingsLog') || 'WhatsApp Auto-Greetings Log'}
        </h2>
        <p className="text-gray-500 mt-1">
          {grandTotal || 0} {t('automatedGreetingsSent') || 'automated greetings sent'}
        </p>
      </div>

      {/* Toolbar — matching ContactsView & LeadsView exactly */}
      <div className="flex flex-col xl:flex-row justify-between gap-3 bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full xl:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="w-full sm:w-80 md:w-96 lg:w-[380px] xl:w-[420px] pl-10 pr-4 py-2 bg-slate-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder={t('searchGreetingsPlaceholder') || 'Search greetings by lead name or phone...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-1.5 w-full sm:w-auto">
            <DatePicker
              className="w-full sm:w-[135px]"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setCurrentPage(1);
              }}
              fullWidth={false}
            />
            <span className="text-gray-400 font-medium">-</span>
            <DatePicker
              className="w-full sm:w-[135px]"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setCurrentPage(1);
              }}
              fullWidth={false}
            />
          </div>
          {(fromDate || toDate) && (
            <button
              type="button"
              onClick={() => {
                setFromDate('');
                setToDate('');
                setCurrentPage(1);
              }}
              className="text-xs font-semibold text-red-500 px-3 h-[40px] hover:bg-red-50 rounded-lg border border-transparent hover:border-red-200 transition-colors flex-shrink-0"
            >
              {t('clear') || 'Clear'}
            </button>
          )}
        </div>

        <div className="flex gap-2 sm:gap-3 w-full xl:w-auto items-center flex-wrap sm:flex-nowrap">
          <button
            type="button"
            onClick={() => handleFilterChange('all')}
            className={`flex-1 sm:flex-none justify-center h-[40px] px-3.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors whitespace-nowrap shadow-sm ${
              filterStatus === 'all'
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-slate-50 text-gray-700 border border-gray-200 hover:bg-slate-100'
            }`}
          >
            <span>{t('all') || 'All'}</span>
            <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full ${
              filterStatus === 'all' ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-700'
            }`} dir="ltr">
              {counts.all || 0}
            </span>
          </button>
          <button
            type="button"
            onClick={() => handleFilterChange('success')}
            className={`flex-1 sm:flex-none justify-center h-[40px] px-3.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors whitespace-nowrap shadow-sm ${
              filterStatus === 'success'
                ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
            }`}
          >
            <span>{t('successStatus') || 'Success'}</span>
            <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full ${
              filterStatus === 'success' ? 'bg-white/20 text-white' : 'bg-emerald-200 text-emerald-800'
            }`} dir="ltr">
              {counts.success || 0}
            </span>
          </button>
          <button
            type="button"
            onClick={() => handleFilterChange('failed')}
            className={`flex-1 sm:flex-none justify-center h-[40px] px-3.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors whitespace-nowrap shadow-sm ${
              filterStatus === 'failed'
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
            }`}
          >
            <span>{t('failedStatus') || 'Failed'}</span>
            <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full ${
              filterStatus === 'failed' ? 'bg-white/20 text-white' : 'bg-red-200 text-red-800'
            }`} dir="ltr">
              {counts.failed || 0}
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Card View — matching ContactsView & LeadsView exactly */}
      <div className={`md:hidden space-y-2 ${loadingLog ? 'opacity-70 pointer-events-none' : ''}`}>
        {loadingLog && greetingsLog.length === 0 ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <Skeleton width={120} height={16} animation="wave" />
                    <Skeleton width={100} height={12} animation="wave" />
                  </div>
                </div>
                <div className="pt-3 border-t flex justify-between">
                  <Skeleton width={80} height={14} animation="wave" />
                  <Skeleton width={60} height={14} animation="wave" />
                </div>
              </div>
            ))}
          </div>
        ) : greetingsLog.length === 0 ? (
          <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm text-center text-gray-400">
            <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-base font-medium">{t('noAutoGreetingsFound') || 'No auto-greetings found'}</p>
          </div>
        ) : (
          greetingsLog.map((log) => (
            <div
              key={log.id}
              onClick={() => navigate(`/conversation-logs?chatId=${log.leadId}`)}
              className="bg-white p-4 rounded-xl border shadow-sm active:bg-gray-50 cursor-pointer space-y-2"
            >
              <div className="flex justify-between items-start">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-gray-900">
                    {log.leadName}
                  </p>
                  <p className="text-xs text-gray-500 font-mono mt-0.5">
                    {log.phone} • Lead #{log.leadId}
                  </p>
                </div>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                  log.status === 'sent' || log.status === 'delivered' || log.status === 'read'
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {log.status}
                </span>
              </div>
              <div className="pt-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                <span>{new Date(log.date).toLocaleString('en-PK', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                <span className="text-blue-600 font-semibold flex items-center gap-1">
                  <MessageCircle className="w-3.5 h-3.5" /> {t('openChat') || 'Open Chat'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View — matching ContactsView & LeadsView exactly */}
      <div className={`hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${loadingLog ? 'opacity-70 pointer-events-none' : ''}`}>
        {loadingLog && greetingsLog.length === 0 ? (
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-4 border-b pb-3">
              {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} width={100} height={16} animation="wave" />)}
            </div>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 py-2 border-b last:border-0">
                <Skeleton width={150} height={16} animation="wave" />
                <Skeleton width={150} height={14} animation="wave" />
                <Skeleton width={100} height={14} animation="wave" />
                <Skeleton width={80} height={14} animation="wave" />
                <Skeleton width={80} height={14} animation="wave" />
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-5 py-4 text-left font-semibold text-gray-600">{t('dateTime') || 'Date & Time'}</th>
                  <th className="px-5 py-4 text-left font-semibold text-gray-600">{t('leadDetails') || 'Lead Details'}</th>
                  <th className="px-5 py-4 text-left font-semibold text-gray-600">{t('whatsappNumber') || 'WhatsApp Number'}</th>
                  <th className="px-5 py-4 text-left font-semibold text-gray-600">{t('deliveryStatus') || 'Delivery Status'}</th>
                  <th className="px-5 py-4 text-left font-semibold text-gray-600">{t('messageId') || 'Message ID'}</th>
                  <th className="px-5 py-4 text-right font-semibold text-gray-600">{t('action') || 'Action'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {greetingsLog.map((log) => (
                  <tr key={log.id} className="hover:bg-blue-50/50 transition-colors">
                    <td className="px-5 py-4 text-gray-600 whitespace-nowrap">
                      <div className="font-medium text-gray-900">
                        {new Date(log.date).toLocaleDateString('en-PK', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(log.date).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {log.leadName}
                        <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-xs font-mono">
                          #{log.leadId}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap font-mono text-gray-700">
                      {log.phone}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                        log.status === 'sent' || log.status === 'delivered' || log.status === 'read'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap font-mono text-xs text-gray-400 truncate max-w-[200px]" title={log.messageId || 'N/A'}>
                      {log.messageId || 'N/A'}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-right">
                      <button
                        type="button"
                        onClick={() => navigate(`/conversation-logs?chatId=${log.leadId}`)}
                        className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all font-semibold text-xs inline-flex items-center gap-1.5"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        {t('openChat') || 'Open Chat'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State — matching ContactsView & LeadsView */}
        {!loadingLog && greetingsLog.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">{t('noAutoGreetingsFound') || 'No auto-greetings found'}</p>
            <p className="text-sm mt-1">
              {searchTerm || filterStatus !== 'all'
                ? (t('noGreetingsMatchFilter') || 'No records match your filter criteria.')
                : (t('systemGeneratedGreetingsAppearHere') || 'System-generated greetings will appear here automatically.')}
            </p>
          </div>
        )}
      </div>

      {/* Numbered Pagination Controls — matching ContactsView exactly */}
      {(totalCount > 0 || greetingsLog.length > 0) && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          {/* Page Size Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t('show') || 'Show'}</span>
            <Dropdown>
              <DropdownTrigger>
                <button
                  type="button"
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center w-[70px]"
                >
                  {pageSize}
                  <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                </button>
              </DropdownTrigger>
              <DropdownPopover className="w-[70px]" placement="top">
                <DropdownMenu
                  selectedKeys={new Set([pageSize.toString()])}
                  selectionMode="single"
                  onSelectionChange={(keys) => handlePageSizeChange(Number(Array.from(keys)[0]))}
                >
                  {[10, 20, 50].map((size) => (
                    <DropdownItem key={size} id={size.toString()}>
                      {size}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </DropdownPopover>
            </Dropdown>
            <span className="text-sm text-gray-600">{t('perPage') || 'per page'}</span>
          </div>

          {/* Page Info */}
          <div className="text-sm text-gray-600">
            {t('page') || 'Page'}{' '}
            <span className="font-semibold text-gray-900">
              {currentPage}
            </span>{' '}
            {t('of') || 'of'}{' '}
            <span className="font-semibold text-gray-900">
              {totalPages}
            </span>
            {' · '}
            <span className="text-gray-500">
              {totalCount} {t('results') || 'results'}
            </span>
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t('firstPage') || 'First page'}
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t('previousPage') || 'Previous page'}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Page Number Buttons */}
            <div className="hidden sm:flex items-center gap-1 mx-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t('nextPage') || 'Next page'}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t('lastPage') || 'Last page'}
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
